import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

import { cva } from '../utils/class-variance.util';
import { ThemeSection } from '../services/theme.service';
import { ButtonVariant, ButtonSize, ButtonTooltipPosition, ButtonColor } from '../models/button.model';

const buttonVariants = cva({
  base: [
    'relative inline-flex items-center justify-center font-medium rounded-xl leading-none',
    'transition-all duration-300 ease-out',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-40',
    'active:scale-95 transform-gpu',
    'overflow-hidden',
    // Glassmorphism base effects
    'backdrop-blur-md',
    'before:content-[""] before:absolute before:inset-0',
    'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
    'before:opacity-50 hover:before:opacity-70',
    'before:transition-opacity before:duration-300',
  ].join(' '),
  variants: {
    variant: {
      bare: [
        // Bare: visually blends into background, no border, no shine or shadow
        'bg-transparent border-0 text-gray-100',
        'dark:text-gray-200',
        'before:opacity-0 hover:before:opacity-0',
        'backdrop-blur-none',
        'shadow-none',
        'hover:bg-transparent',
      ].join(' '),
      outline: [
        // Outline: transparent background with visible border and subtle hover
        'bg-transparent border-2 border-blue-500/80 text-blue-500',
        'hover:bg-blue-600 hover:border-blue-600 hover:text-white',
      ].join(' '),
      default: [
        // Default: subtle neutral button
        'bg-slate-500/70 border border-slate-300/30 text-slate-100',
        'hover:bg-slate-400/80 hover:border-slate-200/45',
      ].join(' '),
      primary: [
        'bg-blue-600 border border-blue-500 text-white',
        'shadow-lg shadow-blue-900/30',
        'hover:bg-blue-700 hover:border-blue-600 hover:text-white',
        'hover:shadow-xl hover:shadow-blue-900/35',
      ].join(' '),
      secondary: [
        'bg-slate-500/75 border border-slate-200/25',
        'text-slate-100 hover:text-white',
        'shadow-md shadow-slate-900/20',
        'hover:bg-slate-400/80 hover:border-slate-100/40',
      ].join(' '),
      ghost: [
        'bg-transparent border border-transparent',
        'text-slate-200 hover:text-white',
        'hover:bg-slate-500/45 hover:border-slate-300/35',
        'hover:backdrop-blur-lg',
      ].join(' '),
      danger: [
        // Light theme
        'bg-red-500/20 border border-red-400/30',
        'text-red-800 hover:text-red-900',
        'shadow-lg shadow-red-500/25',
        'hover:bg-red-500/30 hover:border-red-400/50',
        // Dark theme
        'dark:bg-red-400/25 dark:border-red-300/40',
        'dark:text-red-100 dark:hover:text-white',
        'dark:shadow-lg dark:shadow-red-500/30',
        'dark:hover:bg-red-400/35',
      ].join(' '),
      success: [
        // Light theme
        'bg-cyan-500/20 border border-cyan-400/30',
        'text-cyan-800 hover:text-green-900',
        'shadow-lg shadow-cyan-500/25',
        'hover:bg-cyan-500/30 hover:border-cyan-400/50',
        // Dark theme
        'dark:bg-cyan-400/25 dark:border-cyan-300/40',
        'dark:text-cyan-100 dark:hover:text-white',
        'dark:shadow-lg dark:shadow-cyan-500/30',
        'dark:hover:bg-cyan-400/35',
      ].join(' '),
      subtle: [
        // Subtle: very minimal styling, slight hover effect
        'bg-transparent border border-transparent',
        'text-slate-300 hover:text-white',
        'hover:bg-slate-500/40',
        'before:opacity-0 hover:before:opacity-20',
      ].join(' '),
    },
    size: {
      sm: 'h-9 px-3 text-sm',
      md: 'h-11 px-4 text-base',
      lg: 'h-12 px-6 text-lg',
      xl: 'h-14 px-8 text-xl',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'md',
  },
});

@Component({
  selector: 'ui-button-glass',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './button-glass.component.html',
  styleUrls: ['./button-glass.component.scss'],
})
export class ButtonGlassComponent {
  @Input() variant: ButtonVariant = 'primary';
  @Input() size: ButtonSize = 'md';
  @Input() disabled: boolean = false;
  @Input() loading: boolean = false;
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() ariaLabel: string = '';
  @Input() iconLeft: boolean = false;
  @Input() iconRight: boolean = false;
  @Input() customClass: string = '';
  @Input() themeSection: ThemeSection = 'global';
  @Input() tooltip: string = '';
  @Input() tooltipPosition: ButtonTooltipPosition = 'above';
  @Input() fullWidth: boolean = false;
  // color is optional — when omitted or when set to a non-custom value (e.g. "primary")
  // we should NOT override the variant styles. Only apply custom CSS variable
  // overrides when the provided color matches one of the keys in colorMap.
  @Input() color?: ButtonColor;

  public get computedClasses(): string {
    const baseClasses = buttonVariants({
      variant: this.computedVariant,
      size: this.size,
    });
    const sectionClass = this.themeSection === 'header' ? 'top-shell-control' : '';
    const widthClass = this.fullWidth ? 'w-full' : '';
    const customClasses = this.customClass ? ` ${this.customClass}` : '';
    return `${baseClasses} ${sectionClass} ${widthClass}${customClasses}`.trim();
  }

  /**
   * Compute the effective variant: if the `color` input matches a variant name,
   * prefer that (backwards compatibility for code that used `color="primary"`).
   */
  protected get computedVariant(): ButtonVariant {
    if (!this.color) return this.variant;
    const asString = String(this.color);
    if (this.variantKeys.includes(asString as ButtonVariant)) {
      return asString as ButtonVariant;
    }
    return this.variant;
  }

  private readonly variantKeys: ButtonVariant[] = [
    'primary',
    'secondary',
    'ghost',
    'danger',
    'success',
    'outline',
    'default',
    'bare',
  ];

  public get isCustomColor(): boolean {
    return !!this.color && Object.prototype.hasOwnProperty.call(this.colorMap, this.color as string);
  }

  public get computedStyles(): Record<string, string> {
    if (!this.isCustomColor) return {};
    const map = this.colorMap[this.color as ButtonColor]!;
    return {
      '--btn-bg': map.bg,
      '--btn-border': map.border,
      '--btn-text': map.text,
      '--btn-shadow': map.shadow,
      '--btn-bg-hover': map.bgHover ?? map.bg,
      '--btn-border-hover': map.borderHover ?? map.border,
      '--btn-text-hover': map.textHover ?? map.text,
      '--btn-shadow-hover': map.shadowHover ?? map.shadow,
      '--ripple-color': map.ripple,
    };
  }

  private readonly colorMap: Partial<
    Record<
      ButtonColor,
      {
        bg: string;
        border: string;
        text: string;
        shadow: string;
        ripple: string;
        bgHover?: string;
        borderHover?: string;
        textHover?: string;
        shadowHover?: string;
      }
    >
  > = {
    slate: {
      bg: 'rgba(100,116,139,0.14)',
      border: 'rgba(148,163,184,0.22)',
      text: '#0f172a',
      shadow: 'rgba(15,23,42,0.12)',
      ripple: 'rgba(148,163,184,0.18)',
    },
    stone: {
      bg: 'rgba(120,113,108,0.12)',
      border: 'rgba(161,161,170,0.20)',
      text: '#111827',
      shadow: 'rgba(17,24,39,0.10)',
      ripple: 'rgba(161,161,170,0.16)',
    },
    gray: {
      bg: 'rgba(156,163,175,0.12)',
      border: 'rgba(209,213,219,0.20)',
      text: '#111827',
      shadow: 'rgba(17,24,39,0.10)',
      ripple: 'rgba(209,213,219,0.16)',
    },
    zinc: {
      bg: 'rgba(120,113,108,0.12)',
      border: 'rgba(120,113,108,0.20)',
      text: '#0b1220',
      shadow: 'rgba(11,18,32,0.10)',
      ripple: 'rgba(120,113,108,0.14)',
    },
    navy: {
      bg: 'rgba(24, 41, 68, 0.58)',
      border: 'rgba(124, 153, 196, 0.16)',
      text: '#f5f9ff',
      shadow: 'rgba(2, 8, 23, 0.2)',
      ripple: 'rgba(148, 184, 232, 0.12)',
      bgHover: 'rgba(31, 52, 84, 0.7)',
      borderHover: 'rgba(146, 176, 218, 0.22)',
      textHover: '#ffffff',
      shadowHover: 'rgba(2, 8, 23, 0.28)',
    },
    blue: {
      bg: 'rgba(59,130,246,0.18)',
      border: 'rgba(59,130,246,0.28)',
      text: '#03204f',
      shadow: 'rgba(59,130,246,0.18)',
      ripple: 'rgba(59,130,246,0.22)',
    },
    indigo: {
      bg: 'rgba(99,102,241,0.16)',
      border: 'rgba(99,102,241,0.26)',
      text: '#0b1238',
      shadow: 'rgba(99,102,241,0.16)',
      ripple: 'rgba(99,102,241,0.20)',
    },
    violet: {
      bg: 'rgba(139,92,246,0.14)',
      border: 'rgba(139,92,246,0.24)',
      text: '#2b0b4a',
      shadow: 'rgba(139,92,246,0.14)',
      ripple: 'rgba(139,92,246,0.20)',
    },
    purple: {
      bg: 'rgba(168,85,247,0.14)',
      border: 'rgba(168,85,247,0.24)',
      text: '#2b0b4a',
      shadow: 'rgba(168,85,247,0.14)',
      ripple: 'rgba(168,85,247,0.20)',
    },
    fuchsia: {
      bg: 'rgba(236,72,153,0.12)',
      border: 'rgba(236,72,153,0.22)',
      text: '#3b0520',
      shadow: 'rgba(236,72,153,0.12)',
      ripple: 'rgba(236,72,153,0.18)',
    },
    pink: {
      bg: 'rgba(244,114,182,0.12)',
      border: 'rgba(244,114,182,0.22)',
      text: '#3b0830',
      shadow: 'rgba(244,114,182,0.10)',
      ripple: 'rgba(244,114,182,0.16)',
    },
    rose: {
      bg: 'rgba(244,63,94,0.12)',
      border: 'rgba(244,63,94,0.22)',
      text: '#3b0810',
      shadow: 'rgba(244,63,94,0.10)',
      ripple: 'rgba(244,63,94,0.16)',
    },
    red: {
      bg: 'rgba(239,68,68,0.12)',
      border: 'rgba(239,68,68,0.22)',
      text: '#3b0710',
      shadow: 'rgba(239,68,68,0.12)',
      ripple: 'rgba(239,68,68,0.18)',
    },
    orange: {
      bg: 'rgba(249,115,22,0.12)',
      border: 'rgba(249,115,22,0.22)',
      text: '#3b1600',
      shadow: 'rgba(249,115,22,0.10)',
      ripple: 'rgba(249,115,22,0.16)',
    },
    amber: {
      bg: 'rgba(245,158,11,0.12)',
      border: 'rgba(245,158,11,0.22)',
      text: '#372300',
      shadow: 'rgba(245,158,11,0.10)',
      ripple: 'rgba(245,158,11,0.16)',
    },
    yellow: {
      bg: 'rgba(234,179,8,0.12)',
      border: 'rgba(234,179,8,0.22)',
      text: '#423300',
      shadow: 'rgba(234,179,8,0.10)',
      ripple: 'rgba(234,179,8,0.16)',
    },
    lime: {
      bg: 'rgba(132,204,22,0.12)',
      border: 'rgba(132,204,22,0.22)',
      text: '#0a2a00',
      shadow: 'rgba(16,185,129,0.08)',
      ripple: 'rgba(132,204,22,0.16)',
    },
    green: {
      bg: 'rgba(34,197,94,0.12)',
      border: 'rgba(34,197,94,0.22)',
      text: '#03260a',
      shadow: 'rgba(34,197,94,0.12)',
      ripple: 'rgba(34,197,94,0.18)',
    },
    emerald: {
      bg: 'rgba(16,185,129,0.12)',
      border: 'rgba(16,185,129,0.22)',
      text: '#05261a',
      shadow: 'rgba(16,185,129,0.12)',
      ripple: 'rgba(16,185,129,0.18)',
    },
    teal: {
      bg: 'rgba(20,184,166,0.12)',
      border: 'rgba(20,184,166,0.22)',
      text: '#052827',
      shadow: 'rgba(20,184,166,0.12)',
      ripple: 'rgba(20,184,166,0.18)',
    },
    cyan: {
      bg: 'rgba(6,182,212,0.12)',
      border: 'rgba(6,182,212,0.22)',
      text: '#04282b',
      shadow: 'rgba(6,182,212,0.12)',
      ripple: 'rgba(6,182,212,0.18)',
    },
    sky: {
      bg: 'rgba(56,189,248,0.12)',
      border: 'rgba(56,189,248,0.22)',
      text: '#052737',
      shadow: 'rgba(56,189,248,0.12)',
      ripple: 'rgba(56,189,248,0.18)',
    },
    transparent: {
      bg: 'transparent',
      border: 'transparent',
      text: 'inherit',
      shadow: 'transparent',
      ripple: 'rgba(255,255,255,0.08)',
    },
  };

  protected handleRipple(event: Event): void {
    // Just handle ripple effect, let native (click) event bubble normally
    if (!this.disabled && !this.loading) {
      const button = event.currentTarget as HTMLButtonElement;
      const rect = button.getBoundingClientRect();
      const x = event instanceof MouseEvent ? event.clientX - rect.left : rect.width / 2;
      const y = event instanceof MouseEvent ? event.clientY - rect.top : rect.height / 2;

      button.style.setProperty('--ripple-x', `${x - 50}px`);
      button.style.setProperty('--ripple-y', `${y - 50}px`);
    }
  }

  protected getTooltipPositionClasses(): string {
    const positions: Record<ButtonTooltipPosition, string> = {
      above: 'bottom-full left-1/2 transform -translate-x-1/2 mb-2',
      below: 'top-full left-1/2 transform -translate-x-1/2 mt-2',
      left: 'right-full top-1/2 transform -translate-y-1/2 mr-2',
      right: 'left-full top-1/2 transform -translate-y-1/2 ml-2',
    };
    return positions[this.tooltipPosition];
  }

  protected getTooltipArrowClasses(): string {
    const arrows: Record<ButtonTooltipPosition, string> = {
      above: 'top-full left-1/2 transform -translate-x-1/2 -mt-1',
      below: 'bottom-full left-1/2 transform -translate-x-1/2 -mb-1',
      left: 'left-full top-1/2 transform -translate-y-1/2 -ml-1',
      right: 'right-full top-1/2 transform -translate-y-1/2 -mr-1',
    };
    return arrows[this.tooltipPosition];
  }
}

/**
 * Usage Examples:
 *
 * Basic glassmorphism button:
 * <ui-button-glass>Click me</ui-button-glass>
 *
 * With variants:
 * <ui-button-glass variant="primary" size="lg">Primary Action</ui-button-glass>
 * <ui-button-glass variant="secondary">Secondary</ui-button-glass>
 * <ui-button-glass variant="ghost">Ghost Button</ui-button-glass>
 *
 * With tooltips:
 * <ui-button-glass tooltip="Print product" tooltipPosition="above">Print</ui-button-glass>
 * <ui-button-glass tooltip="Save changes" tooltipPosition="below">Save</ui-button-glass>
 *
 * With icons:
 * <ui-button-glass [iconLeft]="true">
 *   <ui-icon slot="icon-left" name="plus" size="sm"></ui-icon>
 *   Add Product
 * </ui-button-glass>
 *
 * Theme-aware:
 * <ui-button-glass themeSection="header">Header Button</ui-button-glass>
 * <ui-button-glass themeSection="main">Main Content Button</ui-button-glass>
 *
 * Loading state:
 * <ui-button-glass [loading]="isSubmitting">Submit</ui-button-glass>
 */
